import { createStore, createHook, createContainer, createActionsHook } from 'react-sweet-state';
import type { Action } from 'react-sweet-state';

import type { Ari } from '@atlassian/ari';

import {
	confluenceLocalStorageInstance as localStorage,
	keys as localStorageKeys,
} from '@confluence/storage-manager';

import { getBriefing } from '../api/speechAPI';
import type { AudioBriefingRequest } from '../types/audioRequestType';
import type { ActiveAudioContent } from '../types/activeAudioContent';
import type { CustomizationType } from '../types/customizationTypes';
import { CustomTone, CustomLength } from '../types/customizationTypes';
import { getConfluenceContentAri } from '../utils/getConfluenceContentAri';

export type AudioBriefingState = {
	activeContent?: ActiveAudioContent;
	customization: CustomizationType;
	additionalResources: Ari[];
	cloudId: string;
	activationId: string;
	locale: string;
	isLoading: boolean;
	isPlaying: boolean;
	isClosed: boolean;
	isEnded: boolean;
	hasFetched: boolean;
	playbackSpeed: number;
	error?: Error;
	stream?: ReadableStream<Uint8Array>;
	streamEnded: boolean;
};

export type AudioBriefingInitialStateInput = Required<
	Pick<AudioBriefingState, 'activeContent' | 'cloudId' | 'activationId' | 'locale'>
>;

const initialAudioBriefingState: AudioBriefingState = {
	activeContent: undefined,
	customization: {
		tone: CustomTone.CONVERSATIONAL,
		length: CustomLength.SHORT,
	},
	additionalResources: [],
	cloudId: '',
	activationId: '',
	locale: 'en-US',
	isLoading: false,
	isPlaying: false,
	isClosed: false,
	isEnded: false,
	hasFetched: false,
	playbackSpeed: 1,
	stream: undefined,
	error: undefined,
	streamEnded: false,
};

type AudioBriefingContainerProps = {
	initialAudioBriefingState: AudioBriefingInitialStateInput;
};

type AudioBriefingActions = {
	initialize: (
		initialState: AudioBriefingInitialStateInput,
	) => Action<AudioBriefingState, void, void>;
	setAdditionalResources: (newAdditionalResources: Ari[]) => Action<AudioBriefingState, void, void>;
	reset: () => Action<AudioBriefingState, void, void>;
	fetchBriefing: () => Action<AudioBriefingState, void, void>;
	onLoadStart: () => Action<AudioBriefingState, void, void>;
	onLoadEnd: () => Action<AudioBriefingState, void, void>;
	pauseContent: () => Action<AudioBriefingState, void, void>;
	resumeContent: () => Action<AudioBriefingState, void, void>;
	setContentPlaybackSpeed: (speed: number) => Action<AudioBriefingState, void, void>;
	stopContent: () => Action<AudioBriefingState, void, void>;
	endContent: () => Action<AudioBriefingState, void, void>;
	setContentError: (error: Error) => Action<AudioBriefingState, void, void>;
	setStreamEnded: (streamEnded: boolean) => Action<AudioBriefingState, void, void>;
	setCustomization: (
		customization: Partial<CustomizationType>,
	) => Action<AudioBriefingState, void, void>;
};

export const audioBriefingActions: AudioBriefingActions = {
	initialize:
		(initialState: AudioBriefingInitialStateInput) =>
		async ({ setState }) => {
			const emptyAudioBriefingState: AudioBriefingState = {
				stream: undefined,
				streamEnded: false,
				activeContent: undefined,
				customization: {
					tone: CustomTone.CONVERSATIONAL,
					length: CustomLength.SHORT,
				},
				additionalResources: [],
				locale: 'en-US',
				cloudId: '',
				activationId: '',
				isLoading: false,
				isPlaying: false,
				isClosed: false,
				isEnded: false,
				hasFetched: false,
				error: undefined,
				playbackSpeed: Number(localStorage.getItem(localStorageKeys.AI_AUDIO_PLAYBACK_SPEED)) || 1,
			};
			setState(Object.assign(emptyAudioBriefingState, initialState));
		},
	reset:
		() =>
		({ setState }) => {
			setState(initialAudioBriefingState);
		},
	setAdditionalResources:
		(newAdditionalResources: Ari[]) =>
		({ setState }) => {
			setState({
				additionalResources: newAdditionalResources,
			});
		},
	fetchBriefing:
		() =>
		async ({ getState, setState, dispatch }) => {
			dispatch(audioBriefingActions.onLoadStart());

			const { activeContent, additionalResources, cloudId, activationId, locale, customization } =
				getState();

			if (!activeContent) {
				dispatch(
					audioBriefingActions.setContentError(
						new Error('Briefing container needs to be initialized'),
					),
				);
				return;
			}

			const activeContentAri = getConfluenceContentAri(
				cloudId,
				activeContent.id,
				activeContent.contentType,
			);

			if (!activeContentAri) {
				dispatch(audioBriefingActions.setContentError(new Error('Content ARI failed to generate')));
				return;
			}

			const audioRequest: AudioBriefingRequest = {
				activeContentAri,
				additionalResources,
				activationId,
				locale,
				customization,
			};

			try {
				const stream = await getBriefing(audioRequest);

				if (getState().isClosed) {
					return;
				}

				setState({
					isPlaying: false,
					isEnded: false,
					stream,
				});
			} catch (e) {
				setState({
					error: e,
					stream: undefined,
				});
			} finally {
				dispatch(audioBriefingActions.onLoadEnd());
			}
		},
	onLoadStart:
		() =>
		({ setState }) => {
			setState({
				isLoading: true,
				hasFetched: true,
				error: undefined,
				streamEnded: false,
			});
		},
	onLoadEnd:
		() =>
		({ setState }) => {
			setState({ isLoading: false });
		},
	pauseContent:
		() =>
		({ setState }) => {
			setState({
				isPlaying: false,
			});
		},
	resumeContent:
		() =>
		({ setState, getState }) => {
			const { stream } = getState();
			if (!stream) {
				throw new Error('Must fetch before playing');
			}
			setState({
				isPlaying: true,
				isEnded: false,
			});
		},
	setContentPlaybackSpeed:
		(speed: number) =>
		({ setState }) => {
			localStorage.setItem(localStorageKeys.AI_AUDIO_PLAYBACK_SPEED, speed);
			setState({ playbackSpeed: speed });
		},
	stopContent:
		() =>
		async ({ dispatch, setState }) => {
			dispatch(audioBriefingActions.reset());
			setState({ isClosed: true });
		},
	endContent:
		() =>
		({ setState }) => {
			setState({
				isEnded: true,
				isPlaying: false,
			});
		},
	setContentError:
		(error: Error) =>
		({ setState }) => {
			setState({
				error,
			});
		},
	setStreamEnded:
		(streamEnded: boolean) =>
		({ setState }) => {
			setState({
				streamEnded,
			});
		},
	setCustomization:
		(newCustomization: Partial<CustomizationType>) =>
		({ setState, getState }) => {
			const { customization } = getState();
			setState({ customization: { ...customization, ...newCustomization } });
		},
};

const Store = createStore<AudioBriefingState, AudioBriefingActions>({
	initialState: {
		activeContent: undefined,
		customization: {
			tone: CustomTone.CONVERSATIONAL,
			length: CustomLength.SHORT,
		},
		additionalResources: [],
		cloudId: '',
		activationId: '',
		locale: 'en-US',
		isLoading: false,
		isPlaying: false,
		isClosed: false,
		isEnded: false,
		hasFetched: false,
		playbackSpeed: 1,
		error: undefined,
		streamEnded: false,
	},
	actions: audioBriefingActions,
	name: 'listenerState',
});

export const AudioBriefingContainer = createContainer<
	AudioBriefingState,
	AudioBriefingActions,
	AudioBriefingContainerProps
>(Store, {
	onInit:
		() =>
		async ({ dispatch }, { initialAudioBriefingState: initialAudioPlaybackState }) => {
			void dispatch(audioBriefingActions.initialize(initialAudioPlaybackState));
		},
});

export const useAudioBriefing = createHook<AudioBriefingState, AudioBriefingActions>(Store);
export const useAudioBriefingActions = createActionsHook(Store);
